import React, { useEffect } from 'react';
import { AutoSizer, MultiGrid } from 'react-virtualized';
import PatientAdherenceHeaderCell from '../PatientAdherenceHeaderCell';
import PatientAdherenceRowCell from '../PatientAdherenceRowCell';
import { getConfigGrid, getMaxLength } from './utils';
import { IHeaderProperty } from '../utils';
import './styles.scss';

export interface IOwnProps {
  headerColumn: { [key: string]: IHeaderProperty };
  patients: IFormattedPatientType[];
  sortOrder: SortOrderType;
  sortProp: Nullable<string>;
  sortBy: (prop: string) => void | Function;
}
interface ICellRendererProps {
  columnIndex: number;
  key: string;
  rowIndex: number;
  style: Record<string, any>;
}

const VirtualizedComponent = (props: IOwnProps) => {
  const gridRef: any = React.createRef();
  const { headerColumn, patients } = props;
  const configGrid = getConfigGrid(patients);
  const maxLengthList = patients.map((patient) => getMaxLength(patient));

  const cellRenderer = (
    cellRendererProp: ICellRendererProps,
    headerColumn: { [key: string]: IHeaderProperty },
  ) => {
    const { columnIndex, key, rowIndex, style } = cellRendererProp;
    const { patients, sortBy, sortOrder, sortProp } = props;

    if (rowIndex === 0) {
      const headerKey = Object.keys(headerColumn)[columnIndex];
      const headerValue = Object.values(headerColumn)[columnIndex];
      return (
        <div key={key} style={style} styleName="header-cell">
          <PatientAdherenceHeaderCell
            sortProp={sortProp}
            sortBy={sortBy}
            sortOrder={sortOrder}
            headerKey={headerKey}
            headerValue={headerValue}
          />
        </div>
      );
    }

    return (
      <div
        key={key}
        style={style}
        styleName={`${rowIndex % 2 === 0 ? 'row-cell-gray' : 'row-cell-white'}`}
      >
        <PatientAdherenceRowCell
          row={patients[rowIndex - 1]}
          headerKey={Object.keys(headerColumn)[columnIndex]}
        />
      </div>
    );
  };

  useEffect(() => {
    gridRef.current.recomputeGridSize();
  }, [gridRef]);

  const handleRowHeight = ({ index }: { index: number }) => {
    if (index === 0) return 70;
    const lineHeight = 16;
    const totalWordsPerLine = 18;
    const maxLengthPatient = maxLengthList[index - 1];
    const h = ((maxLengthPatient * 1.0) / totalWordsPerLine) * lineHeight;
    return Math.max(h, 43);
  };

  const handleColumnWidth = (
    { index }: { index: number },
    headerColumn: { [key: string]: IHeaderProperty },
  ): number => {
    const headerValue: IHeaderProperty = Object.values(headerColumn)[index];
    return headerValue.width;
  };

  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <MultiGrid
          ref={gridRef}
          cellRenderer={(props) => cellRenderer(props, headerColumn)}
          columnCount={Object.keys(headerColumn).length}
          columnWidth={(props) => handleColumnWidth(props, headerColumn)}
          fixedColumnCount={configGrid.fixedColumnCount}
          fixedRowCount={configGrid.fixedRowCount}
          height={configGrid.height}
          width={width}
          rowCount={configGrid.rowCount}
          rowHeight={handleRowHeight}
          overscanRowCount={10000}
          enableFixedColumnScroll
          enableFixedRowScroll
          hideTopRightGridScrollbar
          hideBottomLeftGridScrollbar
        />
      )}
    </AutoSizer>
  );
};

export default VirtualizedComponent;
